import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { RefObject } from "react";
import { useStore } from "zustand";
import type { Catalogue, CatalogueEntry } from "../catalogue/types";
import type { DrawApi, InfoApi, LayersApi, MapCoreApi, SearchApi, SelectionApi, ViewApi } from "../core/api";
import type { Theme } from "../core/options";
import type { DrawSnapshot, InfoState, LayerSnapshot, MapStoreActions, MapStoreState, SearchState } from "../core/store";
import type { Labels } from "../i18n/labels";
import { useMapCore } from "./coreContext";
import { intlFor, langOf } from "./intl";
import type { Intl2 } from "./intl";
import { buildTreeRows, moveIndex, parentIndex, rowsForRoot, typeaheadIndex } from "./layerTreeModel";
import type { TreeRow, TreeScope } from "./layerTreeModel";

export { useMapCore } from "./coreContext";

export type Breakpoint = "phone" | "narrow" | "desktop" | "wide";

export type ContainerSize = { width: number; height: number; breakpoint: Breakpoint };

export const breakpoints = { phone: 600, narrow: 900, desktop: 1400 } as const;

export function breakpointOf(width: number): Breakpoint {
  if (width <= 0) return "desktop";
  if (width < breakpoints.phone) return "phone";
  if (width < breakpoints.narrow) return "narrow";
  if (width < breakpoints.desktop) return "desktop";

  return "wide";
}

export const LayoutContext = createContext<ContainerSize>({ width: 0, height: 0, breakpoint: "desktop" });

export function useBreakpoint(): Breakpoint {
  return useContext(LayoutContext).breakpoint;
}

export function useMapStore<T>(selector: (state: MapStoreState & MapStoreActions) => T): T {
  const core = useMapCore();

  return useStore(core.store, selector);
}

export function useLabels(): Labels {
  return useMapCore().labels;
}

export function useIntl(): Intl2 {
  const langId = useMapStore(a => a.langId);

  return intlFor(langOf(langId));
}

export function useLayers(): { list: LayerSnapshot[]; api: LayersApi } {
  const core = useMapCore();
  const list = useMapStore(a => a.layers);

  return { list, api: core.layers };
}

export function useSelection(): { ids: number[]; objects: MapStoreState["selection"]["objects"]; api: SelectionApi } {
  const core = useMapCore();
  const selection = useMapStore(a => a.selection);

  return { ids: selection.ids, objects: selection.objects, api: core.selection };
}

export function useDraw(): { snapshot: DrawSnapshot; api: DrawApi } {
  const core = useMapCore();
  const snapshot = useMapStore(a => a.draw);

  return { snapshot, api: core.draw };
}

export function useView(): { view: MapStoreState["view"]; api: ViewApi } {
  const core = useMapCore();
  const view = useMapStore(a => a.view);

  return { view, api: core.view };
}

export function useTheme(): { theme: Theme; dark: boolean; setTheme: (theme: Theme) => void } {
  const core = useMapCore();
  const theme = useMapStore(a => a.theme);
  const dark = useMapStore(a => a.dark);
  const setTheme = useCallback((next: Theme) => core.setTheme(next), [core]);

  return { theme, dark, setTheme };
}

export function useInfo(): { state: InfoState; api: InfoApi } {
  const core = useMapCore();
  const state = useMapStore(a => a.info);

  return { state, api: core.info };
}

export function useSearch(): { state: SearchState; api: SearchApi } {
  const core = useMapCore();
  const state = useMapStore(a => a.search);

  return { state, api: core.search };
}

export function useCatalogue(): Catalogue | null {
  return useMapStore(a => a.catalogue);
}

export function usePanel(): { panel: MapStoreState["panel"]; pinned: boolean; setPanel: (panel: MapStoreState["panel"]) => void; toggle: (panel: Exclude<MapStoreState["panel"], null>) => void } {
  const core = useMapCore();
  const panel = useMapStore(a => a.panel);
  const pinned = useMapStore(a => a.panelPinned);
  const setPanel = useCallback((next: MapStoreState["panel"]) => core.setPanel(next), [core]);
  const toggle = useCallback((next: Exclude<MapStoreState["panel"], null>) => core.setPanel(core.store.getState().panel === next ? null : next), [core]);

  return { panel, pinned, setPanel, toggle };
}

export function useContainerSize(ref: RefObject<HTMLElement | null>): ContainerSize {
  const [size, setSize] = useState<ContainerSize>({ width: 0, height: 0, breakpoint: "desktop" });

  useEffect(() => {
    const element = ref.current;

    if (element === null) return;

    const measure = () => {
      const width = element.clientWidth;
      const height = element.clientHeight;

      setSize(previous => (previous.width === width && previous.height === height ? previous : { width, height, breakpoint: breakpointOf(width) }));
    };

    measure();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);

      return () => window.removeEventListener("resize", measure);
    }

    const observer = new ResizeObserver(measure);

    observer.observe(element);

    return () => observer.disconnect();
  }, [ref]);

  return size;
}

export function useMediaQuery(query: string): boolean {
  const supported = typeof window !== "undefined" && typeof window.matchMedia === "function";
  const [matches, setMatches] = useState(() => (supported ? window.matchMedia(query).matches : false));

  useEffect(() => {
    if (!supported) return;

    const media = window.matchMedia(query);
    const listener = (event: MediaQueryListEvent) => setMatches(event.matches);

    setMatches(media.matches);
    media.addEventListener("change", listener);

    return () => media.removeEventListener("change", listener);
  }, [query, supported]);

  return matches;
}

export function useReducedMotion(): boolean {
  return useMediaQuery("(prefers-reduced-motion: reduce)");
}

export function useCoarsePointer(): boolean {
  return useMediaQuery("(pointer: coarse)");
}

export type LayerTree = {
  rows: TreeRow[];
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  move: (delta: number) => number;
  first: () => number;
  last: () => number;
  typeahead: (text: string) => number;
  parentOf: (index: number) => number;
  setCollapsed: (key: string, collapsed: boolean) => void;
  toggleRow: (row: TreeRow) => void;
  collapseAll: () => void;
  expandAll: () => void;
};

export function useLayerTree(root: TreeScope, filter: string, excludeKeys: string[] = []): LayerTree {
  const core = useMapCore();
  const catalogue = useMapStore(a => a.catalogue);
  const snapshots = useMapStore(a => a.layers);
  const collapsed = useMapStore(a => a.collapsed);
  const [activeIndex, setActiveIndex] = useState(-1);
  const excluded = excludeKeys.join("\u0000");

  const rows = useMemo(() => {
    const skip = new Set(excluded.length === 0 ? [] : excluded.split("\u0000"));
    const entries = rowsForRoot(catalogue, root).filter(a => !skip.has(a.key));

    return buildTreeRows({ entries, root, snapshots, collapsed, filter, byKey: catalogue?.byKey ?? null });
  }, [catalogue, root, snapshots, collapsed, filter, excluded]);

  const setCollapsed = useCallback((key: string, value: boolean) => core.layers.setCollapsed(key.includes(":") ? key : `${root}:${key}`, value), [core, root]);
  const scopeEntries = useCallback(() => rowsForRoot(catalogue, root), [catalogue, root]);

  const toggleRow = useCallback(
    (row: TreeRow) => {
      if (row.isGroup) core.layers.toggleGroup(row.key, row.state !== "all");
      else if (row.snapshot !== null) core.layers.setSelected(row.snapshot.id, row.single ? true : !row.snapshot.selected);
    },
    [core]
  );

  const groupKeys = useCallback(() => {
    const keys: string[] = [];
    const visit = (entries: CatalogueEntry[]) => {
      for (const entry of entries) {
        if (entry.kind === "group") {
          keys.push(`${entry.root}:${entry.key}`);
          visit(entry.children);
        }
      }
    };

    visit(scopeEntries());

    return keys;
  }, [scopeEntries]);

  const collapseAll = useCallback(() => {
    for (const key of groupKeys()) core.layers.setCollapsed(key, true);
  }, [core, groupKeys]);

  const expandAll = useCallback(() => {
    for (const key of groupKeys()) core.layers.setCollapsed(key, false);
  }, [core, groupKeys]);

  return {
    rows,
    activeIndex,
    setActiveIndex,
    move: delta => moveIndex(rows, activeIndex, delta),
    first: () => (rows.length === 0 ? -1 : 0),
    last: () => rows.length - 1,
    typeahead: text => typeaheadIndex(rows, activeIndex, text),
    parentOf: index => parentIndex(rows, index),
    setCollapsed,
    toggleRow,
    collapseAll,
    expandAll
  };
}

export function useForteXMapHandle(): { core: MapCoreApi; layers: LayersApi; selection: SelectionApi; draw: DrawApi; view: ViewApi; info: InfoApi; search: SearchApi } {
  const core = useMapCore();

  return useMemo(() => ({ core, layers: core.layers, selection: core.selection, draw: core.draw, view: core.view, info: core.info, search: core.search }), [core]);
}
